import { Box, Typography, TextField, Button, Link, Alert } from '@mui/material'
import { useState, useEffect } from 'react'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'
import { useResetPasswordMutation } from '@store/api/authApi'

const ResetPassword: React.FC = () => {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  })
  const [error, setError] = useState('')
  const [token, setToken] = useState('')
  const [tokenValid, setTokenValid] = useState(true)
  
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [resetPassword, { isLoading }] = useResetPasswordMutation()

  useEffect(() => {
    const tokenParam = searchParams.get('token')
    if (!tokenParam) {
      setTokenValid(false)
      setError('Invalid reset link. Please request a new password reset.')
    } else {
      setToken(tokenParam)
    }
  }, [searchParams])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
    // Clear error when user starts typing
    if (error) setError('')
  }

  const validatePassword = (password: string): string[] => {
    const errors: string[] = []
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long')
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter')
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter')
    }
    
    if (!/[0-9]/.test(password)) {
      errors.push('Password must contain at least one number')
    }
    
    return errors
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    
    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    // Validate password strength with specific requirements
    const passwordErrors = validatePassword(formData.password)
    if (passwordErrors.length > 0) {
      setError(passwordErrors.join('. '))
      return
    }

    try {
      await resetPassword({ token, password: formData.password }).unwrap()
      // Redirect to login on success
      navigate('/auth/login', { 
        state: { message: 'Password reset successfully. Please login with your new password.' }
      })
    } catch (err: any) {
      const errorMessage = err?.data?.message || err?.message || 'Failed to reset password. Please try again.'
      setError(errorMessage)
    }
  }

  if (!tokenValid) {
    return (
      <Box sx={{ maxWidth: 400, mx: 'auto' }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Invalid Reset Link
        </Typography>
        
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        
        <Box textAlign="center">
          <Link component={RouterLink} to="/auth/forgot-password">
            Request New Reset Link
          </Link>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto' }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Reset Password
      </Typography>
      
      <Typography variant="body2" color="text.secondary" align="center" paragraph>
        Enter your new password below.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        <TextField
          fullWidth
          label="New Password"
          name="password"
          type="password"
          value={formData.password}
          onChange={handleChange}
          margin="normal"
          required
          disabled={isLoading}
          helperText="Password must be at least 8 characters long"
        />
        
        <TextField
          fullWidth
          label="Confirm New Password"
          name="confirmPassword"
          type="password"
          value={formData.confirmPassword}
          onChange={handleChange}
          margin="normal"
          required
          disabled={isLoading}
        />
        
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isLoading}
          sx={{ mt: 3, mb: 2 }}
        >
          {isLoading ? 'Resetting...' : 'Reset Password'}
        </Button>
        
        <Box textAlign="center">
          <Link component={RouterLink} to="/auth/login">
            Back to Login
          </Link>
        </Box>
      </Box>
    </Box>
  )
}

export default ResetPassword
